import React from 'react';
import pluralize from 'pluralize';
import { config } from '@/config';
import { formatNumber } from '@/utils/helpers';
import { FEATURES, checkFeatureAccess } from '@/utils/permissions';
import { POLICIES_SOLD } from '@/components/Index/AF360/utils/constants';

export const DNC_STATUSES = [
  'Do Not Call',
  'Do Not Contact',
  'DNC',
  'DNC/Take Off List',
  'DNC - Do Not Call',
  'Take Off List',
  'TOL - Take Off List',
  'Bad Lead',
  'Deceased'
];

export const CONTACT_TYPES = {
  CONTACT: 'contact',
  TPM: 'tpm',
  FUNERAL_HOMES: 'funeral_homes'
};

export const CONTACT_ORDER_DATE_REFRESHED = { value: 'refreshed_at', label: 'Date Refreshed' };
export const CONTACT_ORDERS = (user = null) =>
  [
    { value: 'created_at', label: config().ASBLEGACY ? 'Date Init' : 'Date Added' },
    { value: 'updated_at', label: 'Date Updated' },
    config().ASBCENTRAL && CONTACT_ORDER_DATE_REFRESHED,
    config().ASBLEGACY && {
      value: 'transferred_at',
      label: 'Date Added'
    },
    config().CFGSNE && {
      value: 'transferred_at',
      label: 'Date Transferred'
    },
    { value: 'first_name', label: 'First Name' },
    { value: 'last_name', label: 'Last Name' },
    { value: 'address', label: 'Address' },
    { value: 'city', label: 'City' },
    { value: 'zip_code', label: 'Zip Code' },
    { value: 'county', label: 'County' },
    config().CLIENTFLOW360 && { value: 'cell_phone', label: 'Mobile' },
    !config().CLIENTFLOW360 && { value: 'phone', label: 'Phone' },
    { value: 'email', label: 'Email' },
    { value: 'dob', label: 'DOB' },
    !config().MULTIPLE_STATUSES && { value: 'status_text', label: 'Status' },
    { value: 'source_text', label: 'Source' },
    { value: 'color', label: 'Color' },
    { value: 'follow_up_date', label: 'Follow Up' },
    config().AGENTFLOW360 && { value: 'course_progress', label: 'Course Progress' },
    config().AGENTFLOW360 && { value: 'course_first_login', label: 'Course First Login' },
    config().AGENTFLOW360 && { value: 'course_last_login', label: 'Course Last Login' },
    config().MYSIBAGENT && {
      value: 'tm_date_created_at',
      label: 'TM Date Create'
    },
    config(user).CONTACTS_SORT_BY_OWNER && {
      value: 'owner_last_name',
      label: 'Owner Last Name'
    }
  ].filter(Boolean);

export const CONTACT_GROUPS = user => {
  let data = [
    { value: 'mine', label: 'Mine' },
    ...(user.roles.includes('view_downline_contacts') ||
    checkFeatureAccess(user.permissions, FEATURES.VIEW_DOWNLINE_CONTACTS).active
      ? [{ value: 'downline', label: 'Downline' }]
      : []),
    ...(!user.basic &&
    checkFeatureAccess(user.permissions, FEATURES.SHARED_CALENDARS).active &&
    !config(user).DISABLED_SHARED_CALENDAR_TAB
      ? [{ value: 'events_contacts', label: 'Shared Calendar' }]
      : []),
    ...(!config().AGENTFLOW360 && !config(user).DISABLED_SHARED_POLICY_TAB
      ? [{ value: 'shared_policy', label: 'Shared Policy' }]
      : [])
  ];

  if (!config().DISABLED_POOLS) {
    data = [...data, { label: 'Pools', value: 'pools', isDisabled: true }];
  }
  return data;
};

export const CONTACT_RM_GROUPS = user => {
  let data = [
    { value: 'mine', label: 'Mine' },
    ...(!config(user).DISABLED_SHARED_CALENDAR_TAB ? [{ value: 'events_contacts', label: 'Shared Calendar' }] : []),
    ...(!config(user).DISABLED_SHARED_POLICY_TAB ? [{ value: 'shared_policy', label: 'Shared Policy' }] : [])
  ];

  if (!config().DISABLED_POOLS) {
    data = [...data, { label: 'Pools', value: 'pools', isDisabled: true }];
  }
  return data;
};

export const DASHBOARD_CONTACT_GROUPS = user => {
  let data = [
    { value: 'mine', label: 'Mine' },
    ...(user.roles.includes('view_downline_contacts') ||
    checkFeatureAccess(user.permissions, FEATURES.VIEW_DOWNLINE_CONTACTS).active
      ? [{ value: 'downline', label: 'Downline' }]
      : []),
    ...(!user.basic &&
    checkFeatureAccess(user.permissions, FEATURES.SHARED_CALENDARS).active &&
    !config(user).DISABLED_SHARED_CALENDAR_TAB
      ? [{ value: 'events_contacts', label: 'Shared Calendar' }]
      : [])
  ];

  if (!config().DISABLED_POOLS) {
    data = [...data, { label: 'Pools', value: 'pools', isDisabled: true }];
  }
  return data;
};

export const DEFAULT_CONTACT_SOURCE = { value: 'general_list', label: 'General List' };
export const DEFAULT_CONTACT_STATUS = { value: 'prospect', label: 'Prospect' };

export const ACTIVITY_STATUSES_HEADER = {
  value: '',
  label: <p className="option-header">Activity Status</p>,
  isDisabled: true
};
export const APPOINTMENT_STATUSES_HEADER = {
  value: '',
  label: <p className="option-header">Appointment Status</p>,
  isDisabled: true
};

export const CLIENT_STATUSES_HEADER = {
  value: '',
  label: <p className="option-header">Client Status</p>,
  isDisabled: true
};

export const splitStatuses = statuses => {
  let activityStatuses = [ACTIVITY_STATUSES_HEADER];
  let appointmentStatuses = [APPOINTMENT_STATUSES_HEADER];
  let clientStatuses = [CLIENT_STATUSES_HEADER];

  const matchedLabels = new Set();

  const pushMatchedStatuses = (sourceArray, targetArray) => {
    sourceArray.forEach(status => {
      let dbStatus = statuses.find(el => el.label === status.label);
      if (dbStatus) {
        targetArray.push(dbStatus);
        matchedLabels.add(dbStatus.label);
      }
    });
  };

  pushMatchedStatuses(ACTIVITY_STATUSES, activityStatuses);
  pushMatchedStatuses(APPOINTMENT_STATUSES, appointmentStatuses);
  pushMatchedStatuses(CLIENT_STATUSES, clientStatuses);

  statuses.forEach(status => {
    if (!matchedLabels.has(status.label)) {
      activityStatuses.push(status);
    }
  });

  return [...activityStatuses, ...appointmentStatuses, ...clientStatuses];
};

export const ACTIVITY_STATUSES = [
  { label: 'Prospect' },
  { label: 'Data Captured' },
  { label: 'Call Back' },
  { label: 'No Interest/Not Set' },
  { label: 'No Answer' },
  { label: 'No Answer 2' },
  { label: 'Disconnect/Wrong#' },
  { label: 'Door Knock' },
  { label: 'Sent Info/Touch 4' },
  { label: 'Referral' },
  { label: 'DNC/Take Off List' },
  { label: 'Deceased' }
];

export const APPOINTMENT_STATUSES = [
  { label: 'Appointment Set' },
  { label: 'Follow Up' },
  { label: 'Fact Finder Done' },
  { label: 'AEP Go-Back' },
  { label: 'Nothing There' },
  { label: 'Not Kept/Porched' }
];

export const CLIENT_STATUSES = [
  { label: 'Submitted/Pending' },
  { label: 'Cancel/Withdrawn' },
  { label: 'Declined' },
  { label: 'Client/Issued' },
  { label: 'Lapsed' },
  { label: 'Deceased Client' }
];

export const CONTACT_DISPOSITIONS = [
  { value: 'active', label: 'Active' },
  { value: 'sold', label: 'Sold' },
  { value: 'no_sale', label: 'No Sale' },
  { value: 'nq_usd', label: 'NQ $' },
  { value: 'nq_health', label: 'NQ Health' },
  { value: 'no_show', label: 'No Show' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'reschedule_needed', label: 'Reschedule Needed' },
  { value: 'aep_fu', label: 'AEP FU' },
  { value: 'agent_fu', label: 'Agent FU' },
  { value: 'cancelled_at_the_door', label: 'Cancelled at the Door' }
];

export const CONTACT_POLICY_STATUSES = (user = null) => {
  if (config().CAREINADVANCE) {
    return [
      { value: 'active', label: 'Active' },
      { value: 'not_policyholder', label: 'Not Policy Holder' },
      { value: 'not_active_policyholder', label: 'Not Active Policy Holder' }
    ];
  }

  if (user?.aris) {
    return [
      { value: 'active_issued', label: 'Active - Issued' },
      { value: 'not_policyholder', label: 'Not Policy Holder' },
      { value: 'not_active_policyholder', label: 'Not Active Policy Holder' },
      { value: 'active_submitted', label: 'Active - Submitted' },
      { value: 'active_policy_holder', label: 'Active Policy Holder' }
    ];
  }

  return [
    { value: 'active_issued', label: 'Active - Issued' },
    { value: 'not_policyholder', label: 'Not Policy Holder' },
    { value: 'not_active_policyholder', label: 'Not Active Policy Holder' },
    { value: 'active_submitted', label: 'Active - Submitted' }
  ];
};

export const DATA_TYPE = [
  { value: 'contact', label: 'Contact' },
  { value: 'spouse_first_name', label: 'Spouse' }
];

export const PLAN_TYPE = (() => {
  if (config().BROKERAGEASB) {
    return [
      { value: 'medsupp', label: 'Med Supp' },
      { value: 'ma', label: 'Med Advantage' }
    ];
  }

  if (config().DATA_CAPTURE_FIELDS) {
    return [
      { value: 'medsupp', label: 'MS' },
      { value: 'ma', label: 'MA' },
      { value: 'pdp', label: 'PDP' },
      { value: 'life', label: 'Life' },
      { value: 'health', label: 'Health' },
      { value: 'annuity', label: 'Annuity' }
    ];
  }
  return [
    { value: 'life', label: 'Life' },
    { value: 'medsupp', label: 'Med Supp' },
    { value: 'ma', label: 'Med Advantage' },
    { value: 'health', label: 'Health' },
    { value: 'annuity', label: 'Annuity' }
  ];
})();

export const COLORS = {
  // do not change downcase of colors to uppercase
  red: '#ff4113',
  orange: '#f39500',
  yellow: '#ffb900',
  green: '#02be4d',
  cyan: '#00c5e4',
  dark_blue: '#334364',
  default: '#ddd'
};

export const SKIP_FIELD = 'skip_this_field';

const ACTIVITY_TRACKING_FIELDS = (user = null) => {
  return !config(user).DISABLED_ACTIVITY_TRACKING
    ? [
        { value: 'attempts', label: 'Attempts' },
        { value: 'contact', label: 'Contact' },
        { value: 'product_info', label: 'Product Info' },
        { value: 'appt_set', label: 'Appt Set' }
      ]
    : [];
};

const AFTERCARE_FIELDS = [
  { value: 'date_of_death', label: 'Date Of Death' },
  { value: 'associated_deceased', label: 'Associated Deceased' },
  { value: 'relationship_to_deceased', label: 'Relationship To Deceased' }
];

const CAREINADVANCE_FIELDS = config().CAREINADVANCE
  ? [...AFTERCARE_FIELDS, { value: 'face_amount', label: 'Face Amount' }]
  : [];

const MYSIBAGENT_FIELDS = config().MYSIBAGENT
  ? [
      { value: 'mr_age', label: 'MrAge' },
      { value: 'mrs_age', label: 'MrsAge' },
      { value: 'ins_comp', label: 'InsComp' },
      { value: 'tm_date_created_at', label: 'TM Date Created' },
      { value: 'tm_uid', label: 'TM ID' }
    ]
  : [];

const REFERRED_BY_FIELD = config().REFERRED_BY ? [{ value: 'referred_by', label: 'Referred By' }] : [];
const COMPANY_FIELD = config().COMPANY_FIELD ? [{ value: 'company', label: 'Company' }] : [];
export const AFFINITY_FIELDS = config().AFFINITY
  ? [
      { value: 'affinity', label: 'Affinity' },
      { value: 'affinity_name', label: 'Affinity Name' },
      { value: 'affinity_campaign', label: 'Affinity Campaign' }
    ]
  : [];
export const CREDIT_UNION_FIELDS = (user = null) => {
  return config(user).CREDIT_UNION ? [{ value: 'credit_union', label: 'Credit Union' }] : [];
};

let agentflowStatusesCache = [];

export const updateAgentflowStatusesCache = statuses => {
  agentflowStatusesCache = statuses || [];
};

export const getStatusGroupLabel = (statusGroupName, defaultLabel) => {
  const statusGroup = agentflowStatusesCache.find(group => group.name === statusGroupName);
  return statusGroup?.title || defaultLabel;
};

export const AF360_FIELDS = (user = null) =>
  config().AGENTFLOW360
    ? [
        {
          value: 'office_location',
          label: config(user).AF360_BU11 ? 'Office Assignment' : 'Office Location'
        },
        { value: 'status_group_1', label: getStatusGroupLabel('status_group_1', 'Communication Status') },
        { value: 'status_group_2', label: getStatusGroupLabel('status_group_2', 'Recruiting Status') },
        { value: 'status_group_3', label: getStatusGroupLabel('status_group_3', 'Hiring Status') }
      ]
    : [];

export const CF360_FIELDS = () => [
  { value: 'cf_status_group_1', label: getStatusGroupLabel('cf_status_group_1', 'Pipeline Statuses') },
  { value: 'cf_status_group_2', label: getStatusGroupLabel('cf_status_group_2', 'Contact Statuses') },
  { value: 'lead_vendor', label: 'Lead Vendor' },
  { value: 'cf_lead_type', label: 'Lead Type' }
];

export const RM_CONTACT_ROLES = [
  { value: 'owner', label: 'Owner' },
  { value: 'vice_president', label: 'Vice President' },
  { value: 'administrator', label: 'Administrator' },
  { value: 'manager', label: 'Manager' },
  { value: 'marketer', label: 'Marketer' }
];

export const LINKED_CONTACTS_ROLES = [
  { value: 'spouse', label: 'Spouse' },
  { value: 'friend', label: 'Friend' },
  { value: 'referral', label: 'Referral' },
  { value: 'family_member', label: 'Family Member' },
  { value: 'coi', label: 'COI' },
  { value: 'other', label: 'Other' }
];

const IMPORT_DATA_CAPTURE_FIELDS = [
  { value: 'data_capture_type', label: 'Data Capture Type' },
  { value: 'data_capture_carrier', label: 'Data Capture Carrier' },
  { value: 'data_capture_premium', label: 'Data Capture Premium' },
  { value: 'data_capture_spouse_type', label: 'Data Capture Spouse Type' },
  { value: 'data_capture_spouse_carrier', label: 'Data Capture Spouse Carrier' },
  { value: 'data_capture_spouse_premium', label: 'Data Capture Spouse Premium' }
];

const IMPORT_DATA_CAPTURE_FIELDS_WITH_DEFAULT = [
  { value: 'data_capture_default', label: 'Data Capture Default' },
  { value: 'data_capture_spouse_default', label: 'Data Capture Spouse Default' },
  ...IMPORT_DATA_CAPTURE_FIELDS
];

export const MEDICARE_POLICY_FIELDS = [
  { value: 'medicare_mbi', label: 'Medicare MBI #' },
  { value: 'medicaid_id', label: 'Medicaid ID #' },
  { value: 'nys_epic', label: 'NYS EPIC' },
  { value: 'low_income_subsidy', label: 'Low Income Subsidy' },
  { value: 'epic_deductible', label: 'EPIC Deductible' }
];

export const FIELDS = (user = null) => {
  return [
    { value: SKIP_FIELD, label: 'Skip this field' },
    { value: 'id', label: 'ID' },
    { value: 'first_name', label: 'First Name' },
    { value: 'last_name', label: 'Last Name' },
    { value: 'address', label: config().CLIENTFLOW360 ? 'Address' : 'Address Line' },
    { value: 'address_2', label: config().CLIENTFLOW360 ? 'Address Cont' : 'Address Line 2' },
    { value: 'state', label: 'State' },
    { value: 'county', label: 'County' },
    { value: 'file_name', label: 'File Name' },
    { value: 'city', label: 'City' },
    { value: 'zip_code', label: 'Zip Code' },
    { value: 'lat', label: 'Latitude' },
    { value: 'lon', label: 'Longitude' },
    { value: 'phone', label: 'Phone' },
    { value: 'cell_phone', label: 'Mobile' },
    { value: 'verified_phone', label: 'Verified Mobile' },
    { value: 'alt2', label: config().MYASBFINANCIAL ? 'Phone 3' : user?.aris ? 'Alt 2' : 'Alt Phone' },
    { value: 'email', label: 'Email' },
    ...(!config().AGENTFLOW360 ? [{ value: 'status_id', label: 'Status' }] : []),
    { value: 'source_id', label: 'Source' },
    { value: 'color', label: 'Color' },
    { value: 'dob', label: 'DOB' },
    ...CAREINADVANCE_FIELDS,
    { value: 'birth_month', label: 'Birth Month' },
    { value: 'birth_day', label: 'Birth Day' },
    { value: 'birth_year', label: 'Birth Year' },
    { value: 'gender', label: 'Gender' },
    { value: 'spouse_dob', label: 'Spouse DOB' },
    { value: 'spouse_gender', label: 'Spouse Gender' },
    ...MYSIBAGENT_FIELDS,
    { value: 'spouse_first_name', label: config().SPOUSE ? 'Spouse First Name' : 'Spouse Name' },
    { value: 'spouse_last_name', label: 'Spouse Last Name' },
    ...(!config().CLIENTFLOW360 ? [{ value: 'spouse_phone', label: 'Spouse Phone' }] : []),
    { value: 'category', label: 'Category' },
    {
      value: 'policy_status',
      label: config().MYASBFINANCIAL ? 'Official ASB Client Designation' : 'Policy Holder Status'
    },
    ...ACTIVITY_TRACKING_FIELDS(user),
    { value: 'products_in_house', label: 'Products Sold' },
    { value: 'notes', label: 'Notes' },
    { value: 'note', label: 'Free Form Notes' },
    { value: 'tags', label: 'Tags' },
    { value: 'text_permission', label: 'Text Permission' },
    { value: 'email_permission', label: 'Email Permission' },
    { value: 'call_permission', label: 'Call Permission' },
    { value: 'attachment', label: 'Attachment' },
    { value: 'funereal_services', label: 'Funeral Service' },
    { value: 'remains_dispositions', label: 'Disposition of Remains' },
    { value: 'languages', label: 'Languages' },
    ...(!config().AGENTFLOW360 && !config().MYCSSAGENT ? [{ value: 'tobacco', label: 'Tobacco Use' }] : []),
    ...(config().MYCSSAGENT ? [{ value: 'appt_setter', label: 'Appt Setter' }] : []),
    ...REFERRED_BY_FIELD,
    ...COMPANY_FIELD,
    ...(config(user).MEDICARE_POLICY_FIELDS ? [{ value: 'ssn', label: 'SSN' }] : []),
    ...(config(user).DATA_CAPTURE_DEFAULT_FIELDS ? IMPORT_DATA_CAPTURE_FIELDS_WITH_DEFAULT : []),
    ...(config(user).DATA_CAPTURE_FIELDS ? IMPORT_DATA_CAPTURE_FIELDS : []),
    ...AF360_FIELDS(user),
    ...(config().CLIENTFLOW360 ? CF360_FIELDS() : []),
    ...(config(user).MAD_LEADS || config(user).MAD_LEAD_TYPE_FIELD
      ? [{ value: 'mad_lead_type', label: 'MAD Lead Type' }]
      : []),
    ...(config(user).MEDICARE_POLICY_FIELDS ? MEDICARE_POLICY_FIELDS : []),
    ...(config(user).AF360_BU11
      ? [
          { value: 'date_licensed', label: 'Date Licensed' },
          { value: 'licensed_agent', label: 'Licensed Agent' }
        ]
      : []),
    ...(config().MYASBAGENT && (config(user).MYASBAGENT_STOCKMAN_OLATEH_KANSAS_COMPANY || config(user).JIMHILL_ADMIN)
      ? [
          { value: 'medicare_part_a_effective_date', label: 'Medicare Part A Effective Date' },
          { value: 'medicare_part_b_effective_date', label: 'Medicare Part B Effective Date' }
        ]
      : [])
  ];
};

const PLAN_POLICY_FIELDS = config().PLAN
  ? [
      { value: 'plan', label: 'Policy Plan' },
      { value: 'plan_id', label: 'Policy Plan ID' }
    ]
  : [];

export const POLICY_FIELDS = (user = null) => {
  if (config().BROKERAGEASB) {
    return [
      { value: 'number', label: 'Policy #' },
      { value: 'policy_type', label: 'Policy Type' },
      { value: 'carrier', label: 'Carrier' },
      { value: 'premium', label: 'Annual Premium' },
      { value: 'effective_date', label: 'Effective Date' },
      { value: 'policy_notes', label: 'Policy Notes' },
      { value: 'status_policy', label: 'Policy Status' },
      { value: 'application_date', label: 'Application Date' }
    ];
  }
  let data = [
    { value: 'number', label: 'Policy #' },
    { value: 'policy_type', label: 'Policy Type' },
    { value: 'policy_source', label: 'Policy Source' },
    { value: 'carrier', label: 'Carrier' },
    { value: 'premium', label: 'Premium' },
    { value: 'sub_premium', label: 'Sub Premium' },
    { value: 'effective_date', label: 'Effective Date' },
    { value: 'mode', label: 'Mode' },
    { value: 'user_1_id', label: 'Writing Agent 1' },
    { value: 'user_2_id', label: 'Writing Agent 2' },
    { value: 'user_3_id', label: 'Writing Agent 3' },
    { value: 'policy_notes', label: 'Policy Notes' },
    ...PLAN_POLICY_FIELDS,
    { value: 'status_policy', label: 'Policy Status' },
    { value: 'sub_source_id', label: 'Sub Source Id' },
    { value: 'iss_source_id', label: 'Iss Source Id' },
    { value: 'est_annualized_comm', label: 'Est Annualized Comm' },
    { value: 'modal_prem_amt', label: 'Modal Prem Amt' },
    { value: 'application_date', label: 'Application Date' },
    { value: 'paid_to_date', label: 'Paid To Date' }
  ];

  if (config().CAREINADVANCE) {
    data = [
      ...data,
      { value: 'insured_name', label: 'Insured Name' },
      { value: 'policy_split', label: 'Policy Split %' },
      { value: 'funeral_home', label: 'Funeral Home' },
      { value: 'funeral_home_id', label: 'Funeral Home ID' },
      { value: 'plan_years', label: 'Plan Years' },
      { value: 'pay_type', label: 'Pay Type' }
    ];
  }
  if (config().CAREINADVANCE || config(user).MYASBAGENT_BERG_MO_ST_LOUIS_COMPANY || config(user).JIMHILL_ADMIN) {
    data = [...data, { value: 'product_description', label: 'Product Description' }];
  }
  if (config(user).MYASBAGENT_BERG_MO_ST_LOUIS_COMPANY || config(user).JIMHILL_ADMIN) {
    data = [...data, { value: 'issued_date', label: 'Issue Date' }, { value: 'term_date', label: 'Term Date' }];
  }
  if (config(user).MYASBAGENT_STOCKMAN_OLATEH_KANSAS_COMPANY || config(user).JIMHILL_ADMIN) {
    data = [...data, { value: 'policy_legacy_id', label: 'Policy Legacy ID' }];
  }
  if (config().MYASBAGENT) {
    data = [...data, { value: 'original_writing_agent_name', label: 'Original Writing Agent Name' }];
  }

  return data;
};

export const CONTACT_PRODUCTS_IN_HOUSE = (user = null) => {
  if (config().CAREINADVANCE) {
    return [
      { value: 'voyage', label: 'Voyage' },
      { value: 'course', label: 'Course' },
      { value: 'annuity', label: 'Annuity' },
      { value: 'family_away_from_home_plan', label: 'Family Away from Home Plan' }
    ];
  }
  if (config().BROKERAGEASB) {
    return [
      { value: 'advantage', label: 'Medicare Advantage' },
      { value: 'supplement', label: 'Medicare Supplement' },
      { value: 'medicare_part_d', label: 'Medicare Part D' },
      { value: 'health_other', label: 'Health (other)' },
      { value: 'other', label: 'Other' }
    ];
  }
  let data = [
    { value: 'life', label: 'Life' },
    { value: 'medsupp', label: 'MedSupp' },
    { value: 'ma', label: 'MA' },
    { value: 'health', label: 'Health' },
    { value: 'annuity', label: 'Annuity' },
    { value: 'other', label: 'Other' }
  ];

  if (config().MYASBAGENT_SYSTEM) {
    data = [...data, { value: 'dsnp', label: 'DSNP' }, { value: 'hospital_indemnity', label: 'Hospital Indemnity' }];
  }
  if (config().ASB_SYSTEM) {
    data = [...data, { value: 'mapd', label: 'MAPD' }];
  }
  if (config(user).PRODUCTS_SOLD_ITS_TRUST) {
    data = [...data, { value: 'its_trust', label: 'ITS Trust' }];
  }

  return data;
};

export const RM_COMMISSIONS = [
  { value: 'F1', label: 'F1' },
  { value: 'F2', label: 'F2' },
  { value: 'F3', label: 'F3' },
  { value: 'F4', label: 'F4' },
  { value: 'F5', label: 'F5' },
  { value: 'F6', label: 'F6' },
  { value: 'AG1', label: 'AG1' },
  { value: 'AG2', label: 'AG2' },
  { value: 'AG3', label: 'AG3' },
  { value: 'AG4', label: 'AG4' },
  { value: 'AC1', label: 'AC1' },
  { value: 'AC2', label: 'AC2' }
];

export const RM_PRODUCTS = [
  { value: 'course', label: 'Course' },
  { value: 'voyage', label: 'Voyage' },
  { value: 'destinations', label: 'Destinations' },
  { value: 'transitions', label: 'Transitions' },
  { value: 'annuity', label: 'Annuity' }
];

export const PLAN_OPTIONS = [
  { value: 'single', label: 'Single' },
  { value: 'one', label: 'One' },
  { value: 'three', label: 'Three' },
  { value: 'five', label: 'Five' },
  { value: 'ten', label: 'Ten' }
];

export const INCLUDE_EXCLUDE = [
  { value: '=', label: '= - include' },
  { value: '!=', label: '!= - exclude' }
];

export const INCLUDE_EXCLUDE_OR = [
  { value: '=', label: '= - include' },
  { value: '!=', label: '!= - exclude' },
  { value: '||=', label: '|| - either/or' }
];

export const SURVIVOR_FIELDS = [
  { value: 'survivor_first_name', label: 'Survivor First Name' },
  { value: 'survivor_last_name', label: 'Survivor Last Name' },
  { value: 'mobile_phone', label: 'Mobile Phone' },
  { value: 'relationship', label: 'Relationship' }
];

export const previewLabel = (place, label, user = null) => {
  switch (place) {
    case 'products_in_house':
      return label
        .map(product => (CONTACT_PRODUCTS_IN_HOUSE(user).find(({ value }) => value === product) || {}).label)
        .join(', ');
    case 'policy_status':
      return (CONTACT_POLICY_STATUSES(user).find(el => el.value === label) || {}).label;
    case 'disposition':
      return (CONTACT_DISPOSITIONS.find(el => el.value === label) || {}).label;
    case 'lead_type':
      return (LEAD_TYPES.find(el => el.value === label) || {}).label;
    case 'campaign_type':
      return (CAMPAIGN_TYPES.find(el => el.value === label) || {}).label;
    case 'allforce':
      return (ALLFORCE.find(el => el.value === label) || {}).label;
    case 'tobacco':
      return (TOBACCO.find(el => el.value === label) || {}).label;
    case 'policy_owned':
      return (POLICIES_SOLD.find(el => el.value === label) || {}).label;
    default:
      return '';
  }
};

export const SURVIVOR = (() => {
  let survivors_params = [];
  ['1', '2', '3', '4', '5', '6', '7'].forEach(index =>
    SURVIVOR_FIELDS.forEach(survivor =>
      survivors_params.push({ value: `${survivor.value}_${index}`, label: `${survivor.label} ${index}` })
    )
  );
  return survivors_params;
})();

export const MONTHS = [
  { value: '1', label: 'January' },
  { value: '2', label: 'February' },
  { value: '3', label: 'March' },
  { value: '4', label: 'April' },
  { value: '5', label: 'May' },
  { value: '6', label: 'June' },
  { value: '7', label: 'July' },
  { value: '8', label: 'August' },
  { value: '9', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' }
];

export const GENDERS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' }
];

export const LICENSED_AGENTS = (user = null) => {
  if (config(user).AF360_BU11) {
    return [
      { value: 'yes', label: 'Yes' },
      { value: 'life_and_health', label: 'Life and Health' },
      { value: 'life_only', label: 'Life Only' },
      { value: 'health_only', label: 'Health Only' },
      { value: 'p_and_c', label: 'P and C' },
      { value: 'no', label: 'No' }
    ]; // Licensed Agent for Agentflow360 business unit ASB Financial(number BU11)
  }
  return [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
    { value: 'unknown', label: 'Unknown' }
  ];
};

export const REMOVE_FROM_MILLS = [
  { value: 'mia', label: 'MIA' },
  { value: 'didnt_complete_course', label: "Didn't complete course" },
  { value: 'failed_test', label: 'Failed test' },
  { value: 'took_another_position', label: 'Took another position' },
  { value: 'termed_after_hired', label: 'Termed after hired' }
];

const YES_OR_NO = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' }
];

export const AFFINITY = YES_OR_NO;
export const AFFINITY_NAMES = [
  { value: 'AMVETS', label: 'AMVETS' },
  { value: 'Elks', label: 'Elks' },
  { value: 'The American Legion', label: 'The American Legion' },
  { value: 'Fraternal Order of Eagles', label: 'Fraternal Order of Eagles' },
  { value: 'SUAA', label: 'SUAA' },
  { value: 'Professional Association of Georgia Educators', label: 'Professional Association of Georgia Educators' },
  { value: 'Second Amendment Foundation', label: 'Second Amendment Foundation' }
];
export const AML = YES_OR_NO;

export const RELATIONSHIP_OPTIONS = [
  { value: 'spouse', label: 'Spouse' },
  { value: 'child', label: 'Child' },
  { value: 'parent', label: 'Parent' },
  { value: 'family', label: 'Family' },
  { value: 'other', label: 'Other' }
];

export const ARIS_FIELDS = [
  {
    value: 'replacement',
    label: 'Replacement'
  }
];

export const CF_360_CATEGORIES = [
  { value: 'cf360_client', label: 'Client' },
  { value: 'cf360_lead', label: 'Lead' },
  { value: 'cf360_prospect', label: 'Prospect' },
  { value: 'cf360_other', label: 'Other' }
];

export const CF_LEAD_TYPES = [
  { value: 'Mail Lead', label: 'Mail Lead' },
  { value: 'Digital Lead', label: 'Digital Lead' },
  { value: 'Seminar', label: 'Seminar' },
  { value: 'Referral', label: 'Referral' },
  { value: 'Internet Lead', label: 'Internet Lead' },
  { value: 'Agent Lead', label: 'Agent Lead' },
  { value: 'TM Lead', label: 'TM Lead' }
];

export const MAD_LEAD_TYPES = [
  { value: 't65_mail', label: 'T65 Mail' },
  { value: 't65_qr', label: 'T65 QR' },
  { value: 'ms_mail', label: 'MS Mail' },
  { value: 'ms_qr', label: 'MS QR' }
];

export const CF360_MAX_IMPORT_CONTACTS_COUNT = Object.freeze([
  { value: 'core', count: 5000 },
  { value: 'marketing', count: 50000 },
  { value: 'agency', count: 250000 }
]);

export const normalizeCf360PlanType = planType => {
  if (planType === 'agency_membership') return 'agency';
  if (CF360_MAX_IMPORT_CONTACTS_COUNT.some(({ value }) => value === planType)) return planType;
  return 'core';
};

export const getCf360UserPlanType = user => normalizeCf360PlanType(user?.plan_type);

export const getCf360ImportLimit = user => {
  const planType = getCf360UserPlanType(user);
  return CF360_MAX_IMPORT_CONTACTS_COUNT.find(({ value }) => value === planType)?.count;
};

export const getCf360ImportLimitError = (contactsLength, user) => {
  const limit = getCf360ImportLimit(user);
  if (contactsLength <= limit) return null;

  return `You're trying to import ${formatNumber(contactsLength)} ${pluralize('contact', contactsLength)}, but your current plan allows a maximum of ${formatNumber(limit)} contacts. Please upgrade your plan to import more contacts.`;
};

export const OWNER_FIELD = { value: 'user_id', label: 'Owner' };
export const LEGACY_ID_FIELD = { value: 'legacy_id', label: 'Legacy ID' };

export const IMPORT_FILTERS = (() => {
  let data = [
    { value: '', label: 'All' },
    { value: 'title', label: 'Title' },
    { value: 'status', label: 'Status' },
    { value: 'importer', label: 'Importer' },
    { value: 'owner', label: 'Owner' },
    { value: 'created_at', label: 'Date Added' }
  ];
  if (!config().DISABLED_POOLS) {
    data = [...data, { value: 'pool', label: 'Pool' }];
  }
  return data;
})();

export const IMPORT_STATUSES = [
  { value: 'completed', label: 'Completed', color: '#02Be4d', background_color: 'rgba(2, 190, 77, 0.2)' },
  { value: 'init', label: 'Initialized', color: '#b2bac6', background_color: 'rgba(178, 186, 198, 0.2)' },
  { value: 'pending', label: 'Pending', color: '#b2bac6', background_color: 'rgba(178, 186, 198, 0.2)' },
  { value: 'failed', label: 'Failed', color: '#f9422d', background_color: 'rgba(249, 66, 45, 0.2)' },
  { value: 'importing', label: 'Importing', color: '#fdd835', background_color: 'rgba(253, 216, 53, 0.2)' }
];

export const getExcludedImports = currentUser => {
  let excludedFields = [];
  const IMPORT_ONLY_ADMIN = [...AFFINITY_FIELDS];

  if (!currentUser.admin) excludedFields = [...excludedFields, ...IMPORT_ONLY_ADMIN];
  return excludedFields;
};

export const TABS_FOR_DISABLED_CONTACT = ['all_agents', 'events_contacts'];

export const ADDITIONAL_DETAILS_GIVEN_TO = [
  { value: 'Joel', label: 'Joel' },
  { value: 'Marc Macke', label: 'Marc Macke' },
  { value: 'Do not refer', label: 'Do not refer' }
];

export const ADDITIONAL_DETAILS_STATUSES = [
  { value: 'sold', label: 'Sold' },
  { value: 'not_sold', label: 'Not Sold' }
];

export const SECURITIES_TYPE = [
  { value: 'IRA', label: 'IRA' },
  { value: 'Joint', label: 'Joint' },
  { value: 'Roth IRA', label: 'Roth IRA' }
];

export const SECURITIES_SOURCE = [
  { value: 'Existing', label: 'Existing' },
  { value: 'ASB', label: 'ASB' }
];

export const FUNERAL_SERVICE = [
  { value: 'traditional_funeral_service', label: 'Traditional Funeral Service' },
  { value: 'graveside_service', label: 'Graveside Service' },
  { value: 'immediate_burial', label: 'Immediate Burial (No Service)' },
  { value: 'traditional_service_followed_by_cremation', label: 'Traditional Service followed by Cremation' },
  { value: 'cremation_with_memorial_service', label: 'Cremation with Memorial Service' },
  { value: 'direct_cremation', label: 'Direct Cremation (No Service)' },
  { value: 'traditional_funeral_service_ship_out', label: 'Traditional Funeral Service / Ship Out' },
  { value: 'direct_ship_out', label: 'Direct Ship Out (No Service)' },
  { value: 'other', label: 'Other/Various' }
];

export const DISPOSITION_OF_REMAINS = [
  { value: 'ground_burial', label: 'Ground Burial' },
  { value: 'natural_burial', label: 'Green/Natural Burial' },
  { value: 'urn_plot', label: 'Urn Plot' },
  { value: 'mausoleum_crypt', label: 'Mausoleum Crypt' },
  { value: 'niche', label: 'Niche' },
  { value: 'scattering_garden', label: 'Scattering Garden' },
  { value: 'scattering_at_sea', label: 'Scattering at Sea' }
];

export const LANGUAGE = [
  { value: 'english', label: 'English' },
  { value: 'spanish', label: 'Spanish' },
  { value: 'chinese', label: 'Chinese' },
  { value: 'japanese', label: 'Japanese' },
  { value: 'korean', label: 'Korean' },
  { value: 'other', label: 'Other' }
];

export const RX_DETAILS_PLAN_TYPES = [
  { value: 'ma', label: 'Medicare Advantage' },
  { value: 'part_d', label: 'Part D' },
  { value: 'aca', label: 'ACA' }
];

export const RX_DETAILS_RECORD_TYPES = [
  { value: 'change', label: 'Change' },
  { value: 'no_change', label: 'No Change' },
  { value: 'not_selected', label: 'Not Selected' }
];

export const RX_DETAILS_LETTER_SENT = [
  { value: 'complete', label: 'Complete' },
  { value: 'incomplete', label: 'Incomplete' }
];

export const RX_DETAILS_FILL_SUPPLIES = [
  { value: '30', label: '30' },
  { value: '90', label: '90' },
  { value: '180', label: '180' }
];

export const IMPORT_FIELD_LIMIT = {
  first_name: 30,
  last_name: 30,
  middle_name: 30,
  database_field: 30,
  status_id: 30,
  suffix: 30,
  nick_name: 30,
  address: 30,
  address_2: 30,
  county: 30,
  file_name: 250,
  city: 30,
  associated_deceased: 60,
  spouse_first_name: 30,
  spouse_last_name: 30,
  color: 30,
  note: 70000
};

export const LEAD_TYPES = [
  { label: 'FE', value: 'fe' },
  { label: 'FE Underwriting', value: 'fe_underwriting' },
  { label: 'FE Death Benefit', value: 'fe_death_benefit' },
  { label: 'FE Premium', value: 'fe_premium' }
];

export const CAMPAIGN_TYPES = [
  { label: 'Preset Appt', value: 'preset_appt' },
  { label: 'Internet Lead', value: 'internet_lead' }
];

export const ALLFORCE = [
  { label: 'Yes', value: true },
  { label: 'No', value: false }
];

export const MEDICARE_REVIEW_IMPROVEMENT_OPTIONS = [
  { value: 'doctor_network', label: 'Doctor Network' },
  { value: 'prescription_costs', label: 'Prescription Costs' },
  { value: 'premiums_copays', label: 'Premiums or Co-pays associated with your program' },
  { value: 'other', label: 'Other' }
];

export const YES_NO_OPTIONS = [
  { value: true, label: 'Yes' },
  { value: false, label: 'No' }
];
export const TOBACCO = YES_NO_OPTIONS;
export const TM_UIDS = [
  { value: 'Lisa Smith', label: 'Lisa Smith' },
  { value: 'Rebekah Rickman', label: 'Rebekah Rickman' },
  { value: 'Senior Ins Brokers', label: 'Senior Ins Brokers' },
  { value: 'Dean Olberding', label: 'Dean Olberding' },
  { value: 'Crystal William', label: 'Crystal William' },
  { value: 'Keyshawn Jackson', label: 'Keyshawn Jackson' },
  { value: 'Medicare 101', label: 'Medicare 101' },
  { value: 'Facebook', label: 'Facebook' },
  { value: 'Life Leads', label: 'Life Leads' },
  { value: 'Woosender', label: 'Woosender' },
  { value: '55 + Community', label: '55 + Community' },
  { value: 'Pharmacy Referral', label: 'Pharmacy Referral' },
  { value: 'Centerwell Expo', label: 'Centerwell Expo' }
];

/** Parses comma-separated appt_setters setting value into options for selects. */
export const apptSettersValueToOptions = value =>
  (value || '')
    .split(',')
    .map(s => s.trim())
    .filter(Boolean)
    .map(s => ({ value: s, label: s }));

export const appTrackerLink = (user, contact = null) => {
  if (config().IS_DEMO && ['7985'].includes(user?.number)) {
    return `https://apptracker.mymaddevelopment.com/submitted/start?agent_id=${user?.number}&lead_id=${contact?.number}`;
  }

  if (config().MYMADCRM && ['C19', 'C20'].includes(user?.company?.number)) {
    return `https://apptracker.mymadcrm.com/submitted/start?agent_id=${user?.number}&lead_id=${contact?.number}`;
  }

  if (user?.aris) {
    return `https://apptracker.aegleads.com/submitted/start?agent_id=${user?.number}&lead_id=${contact?.number}`;
  }

  return;
};

export const phoneFieldName = (user, field) => {
  switch (field) {
    case 'email':
      return 'Email';
    case 'phone':
      return 'Phone';
    case 'cell_phone':
      return user?.aris ? 'Alt 1' : 'Mobile';
    case 'alt2':
      return config().MYASBFINANCIAL ? 'Phone 3' : user?.aris ? 'Alt 2' : 'Alt Phone';
  }
};
